import React from 'react';

import { useReduxForm } from 'global/lib/reduxForm';
import { useSelector, useDispatch } from 'react-redux';

import { useMessages } from 'component-utilities/intl';
import {
  FormContainer,
  FormTitle,
  FormRow,
  FormItemLarge,
  FormItemSmall,
  FormLabel,
  InputField,
  Textarea,
} from 'v-c/Forms';
import { PrimaryButton, SecondaryButton, Spacer } from 'v-c/Buttons';

import { jsUi } from '../../global/lib/jsUi';
import { useRouteLeaveHook } from '../../global/lib/routeLeaveHook';
import Breadcrumb from '../../global/components/Breadcrumb';
import FlashMessages, {
  useFlashConnect,
  PAGES,
} from '../../global/components/FlashMessages';
import {
  createLegalHoldReason,
  resetLegalHoldReasons,
} from '../../global/state_managers/LegalHoldReasonsStateManager';
import { resetFlash } from '../../global/state_managers/FlashMessagesStateManager';
import { breadcrumbsActions } from '../../global/state_managers/BreadcrumbsStateManager';

import { stateLogic } from './NewHoldReasonDeconstructed';

const BASE_KEY = 'app.new_hold_reason';

// Inner component that receives props from stateLogic HOC
function NewHoldReasonInner(props: any) {
  const {
    getMessage,
    fields,
    onCancel,
    dirty,
    onSave,
    isValidForm,
  } = props;

  return (
    <div data-view="new-hold-reason">
      <div data-content="header">
        <FlashMessages />
        <Breadcrumb {...props} />
      </div>
      <div data-content="content">
        <FormContainer>
          <FormTitle>{getMessage(`${BASE_KEY}.title`)}</FormTitle>
          <FormRow>
            <FormItemLarge>
              <FormLabel required>{getMessage('app.general.name')}</FormLabel>
              <InputField
                focused
                data-field="new-reason-name"
                {...fields.name}
              />
            </FormItemLarge>
          </FormRow>
          <FormRow>
            <FormItemLarge>
              <FormLabel required>
                {getMessage('app.general.description')}
              </FormLabel>
              <Textarea
                focused={false}
                data-field="new-reason-description"
                {...fields.description}
              />
            </FormItemLarge>
          </FormRow>
          <FormRow>
            <FormItemSmall>
              <PrimaryButton
                data-action="save-reason"
                disabled={!dirty || !isValidForm}
                onClick={onSave}
                loading={false}
              >
                {getMessage('app.button.save')}
              </PrimaryButton>
              <Spacer />
              <SecondaryButton data-action="cancel-reason" onClick={onCancel}>
                {getMessage('app.button.cancel')}
              </SecondaryButton>
            </FormItemSmall>
          </FormRow>
        </FormContainer>
      </div>
    </div>
  );
}

// Apply stateLogic HOC
const NewHoldReasonWithState = stateLogic({
  goToReasonsIndex: jsUi.holdReasonsIndex.goTo,
})(NewHoldReasonInner);

// Main component with hooks
function NewHoldReason() {
  const dispatch = useDispatch();
  const getMessage = useMessages();
  
  const legalHoldReasons = useSelector((state: any) => state.legalHoldReasons);
  const flashMessages = useSelector((state: any) => state.flashMessages);

  // The stateLogic component calls props.setRouterWillLeaveValidator on mount,
  // so the route/leave props from the hook must be forwarded — discarding them
  // crashes the page (setRouterWillLeaveValidator is undefined).
  const routeLeaveProps = useRouteLeaveHook();
  useFlashConnect(PAGES.NEW_HOLD_REASON, 'New-hold-reason');

  const formProps = useReduxForm({
    form: 'new-hold-reason',
    fields: ['name', 'description'],
    initialState: { name: '', description: '' },
  });

  const props = {
    ...formProps,
    ...routeLeaveProps,
    getMessage,
    legalHoldReasons,
    flashMessages,
    createLegalHoldReason: (values: any) => dispatch(createLegalHoldReason(values)),
    resetLegalHoldReasons: (opts: any) => dispatch(resetLegalHoldReasons(opts)),
    resetFlash: (opts: any) => dispatch(resetFlash(opts)),
    setBreadcrumbForHoldReasonsNewReasonPage: () =>
      dispatch(breadcrumbsActions.holdReasonsNewReasonPage()),
  };

  return <NewHoldReasonWithState {...props} />;
}

export default NewHoldReason;
